import { americanToDecimal, decimalToAmerican } from "@/utils/calculators/odds-utils";
import { getOfficialPreset, type BookWeights } from "@/lib/weights-config";

export const YOUTUBE_KALSHI = {
  videoId: "FrlGLcsEsgg",
  title: "Kalshi College Football Strategy: 3 Steps to ACTUALLY profit",
  slug: "kalshi-college-football",
  path: "/learning-center/watch/kalshi-college-football",
  flag: "youtube_kalshi_cfb_walkthrough_v1",
  exposure: "youtube_walkthrough_experiment_entered",
  version: "kalshi_cfb_walkthrough_v1",
  tool: "/ev-sniper",
} as const;

export const YOUTUBE_KALSHI_QUERY = new URLSearchParams({
  utm_source: "youtube", utm_medium: "organic_video",
  utm_campaign: YOUTUBE_KALSHI.flag, utm_content: YOUTUBE_KALSHI.videoId,
  placement: "video", via: "matt",
}).toString();

export function getYoutubeKalshiCampaign(query: URLSearchParams, lesson: { flag: string; videoId: string } = YOUTUBE_KALSHI) {
  const expected = new URLSearchParams({ utm_source: "youtube", utm_medium: "organic_video", utm_campaign: lesson.flag, utm_content: lesson.videoId, placement: "video", via: "matt" });
  for (const [key, value] of expected) {
    if (query.getAll(key).length !== 1 || query.get(key) !== value) return null;
  }
  if (query.has("preview")) return null;
  return { videoId: lesson.videoId, creator: "matt", placement: "video" } as const;
}

export type PracticeBook = { id: string; name: string; yes: number; no: number };
export type PracticeMarket = {
  id: string; game: string; outcome: string; kind: "Spread" | "Total";
  price: number; contracts: number; books: PracticeBook[];
};

export const PRACTICE_MARKETS: PracticeMarket[] = [
  {
    id: "tarleton", game: "Merrimack vs Tarleton State", outcome: "Tarleton State −29.5", kind: "Spread", price: 56, contracts: 180,
    books: [{ id: "pinnacle", name: "Pinnacle", yes: -160, no: 135 }, { id: "circa", name: "Circa", yes: -165, no: 140 }, { id: "draftkings", name: "DraftKings", yes: -155, no: 125 }, { id: "fanduel", name: "FanDuel", yes: -160, no: 130 }],
  },
  {
    id: "vanderbilt", game: "Vanderbilt vs Auburn", outcome: "Over 41.5 points", kind: "Total", price: 81, contracts: 60,
    books: [{ id: "pinnacle", name: "Pinnacle", yes: -680, no: 510 }, { id: "circa", name: "Circa", yes: -700, no: 525 }, { id: "draftkings", name: "DraftKings", yes: -650, no: 450 }, { id: "fanduel", name: "FanDuel", yes: -660, no: 475 }],
  },
  {
    id: "ole-miss", game: "Ole Miss vs Florida", outcome: "Over 47.5 points", kind: "Total", price: 79, contracts: 100,
    books: [{ id: "pinnacle", name: "Pinnacle", yes: -650, no: 500 }, { id: "circa", name: "Circa", yes: -700, no: 520 }, { id: "draftkings", name: "DraftKings", yes: -600, no: 425 }, { id: "fanduel", name: "FanDuel", yes: -625, no: 450 }],
  },
];

export const MATT_PRACTICE_WEIGHTS: BookWeights = { ...getOfficialPreset("matt")!.weights };

export function practiceFairProbability(market: PracticeMarket, weights: BookWeights = {}) {
  let weighted = 0;
  let total = 0;
  for (const book of market.books) {
    const yes = 1 / americanToDecimal(book.yes);
    const no = 1 / americanToDecimal(book.no);
    const weight = weights[book.id] ?? 1;
    weighted += (yes / (yes + no)) * weight;
    total += weight;
  }
  return total > 0 ? weighted / total : null;
}

export function practiceOrder(budgetDollars: number, priceCents: number, available: number, fairProbability: number) {
  if (!Number.isFinite(budgetDollars) || budgetDollars <= 0 || budgetDollars > 10000 || !Number.isInteger(priceCents) || priceCents < 1 || priceCents > 99 || !Number.isInteger(available) || available < 1 || !Number.isFinite(fairProbability) || fairProbability <= 0 || fairProbability >= 1) return null;
  const budget = Math.floor(budgetDollars * 100 + 0.000001);
  const contracts = Math.floor(budget / priceCents);
  if (contracts < 1) return null;
  const cost = contracts * priceCents;
  const expectedProfit = contracts * fairProbability - cost / 100;
  return { contracts, cost, payout: contracts * 100, profitIfYes: contracts * 100 - cost, expectedProfit, evPercent: expectedProfit / (cost / 100) * 100, sufficientLiquidity: contracts <= available };
}

export function practiceOdds(priceCents: number) { return decimalToAmerican(100 / priceCents); }
export function displayOdds(odds: number) { return odds > 0 ? `+${odds}` : `−${Math.abs(odds)}`; }
export function dollars(cents: number) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100); }

export const WALKTHROUGH_STEPS = [
  { label: "Read the board", title: "Read the board. Make it match Matt’s.", instruction: "Kalshi and Football are selected, and the price comparison is already open. Read the three pointers, then apply Matt’s weights to move straight to your practice bet.", chapter: 567 },
  { label: "One-click bet", title: "From the Sniper straight to the Kalshi market.", instruction: "One click opens the matching market. Then you choose an amount, review the order and confirm. We’ll practice that handoff here.", chapter: 744 },
  { label: "Whale Watch", title: "A separate tool: follow the exchange footprint.", instruction: "Now leave the Sniper and explore Whale Watch. Read the posted money, its price and where you can get a better offer.", chapter: 452 },
  { label: "Ready to explore", title: "Two tools. One price-first process.", instruction: "You’ve read the Sniper, used Matt’s weights, practiced a Kalshi order and explored Whale Watch.", chapter: 883 },
] as const;
